import React, { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import "../../scss/registration/registration.scss";
import {
    validateForm,
    handleSSNInput,
    handleDOBInput,
    validateEmail,
    validatePassword,
    validateLatinCharacters,
    validatePostalCode,
    validatePhoneNumber,
} from '../../utils/registration-validation';
import { FreelancerIcon, BusinessIcon, Calendar, ShieldIcon, bankidcolor } from "../../components/images/svg/icons"
import BankIDSignupInline from '../../components/BankIDSignupInline/BankIDSignupInline';
import { Col, Container, Row } from "react-bootstrap";
import { EconomicCard } from "../../components/economic-card.component"
import { UIInput } from "../../components/ui-input";
import { socialLogins } from "../../constant/sign-up.constant";
import { LOCALES } from "../../../common/constants";
import { Link, useNavigate } from "react-router-dom";
import { getItem, getLocalizedPathFromLocalStorage, setItem } from "../../helper/localstorage.helper";
import { CalendarSingle } from '../../components/calendar';

import { useDispatch, useSelector } from "react-redux";
import { prepareRegistrationPayload } from "../../helper/signup.helpers";
import { registerUser } from "../../features/signup/signup.slice";
import { blobURL } from "../../../common/StringUtils";
import { signInWithEmailAndPassword } from "../../features/signin/signin.slice";
import { RegistrationHeader } from "../../components/registeration-header-.component";
import useOutsideClick from '../../hooks/useOutsideClick';
import { renderCountryOptionsWithI18n } from "../../utils/countries";
import CryptoJS from "crypto-js";

interface RegistrationFormData {
    firstName: string;
    lastName: string;
    email: string;
    password: string;
    zipCode: string;
    country: string;
    address: string;
    socialSecurityNumber: string;
    dateOfBirth: string;
    agreeToTerms: boolean;
    noIdNumber: boolean;
    phoneNo?: string;
    accountType: 'freelancer' | 'business';
    ReferredByUserId?: string;
    ReferredByCode?: string;
}

interface FormErrors {
    [key: string]: string;
}

export const Registration: React.FC = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const dispatch = useDispatch() as any;
    const { loading: isSubmitting, error: submitError } = useSelector((state: any) => state.signup);
    const [accountType, setAccountType] = useState<'freelancer' | 'business'>('freelancer');
    const [loading, setLoading] = useState(false);
    const [signupMode, setSignupMode] = useState<'bankid-active' | 'email'>('bankid-active');
    const [showDOBCalendar, setShowDOBCalendar] = useState(false);
    const currentLanguage = new URLSearchParams(location.search).get('lang') || 'sv-se';
    const invoicePDF = `${blobURL}/invoice.pdf`;
    const invoiceSwidishPDF = `${blobURL}/invoiceSwidish.pdf`;
    const workAgrementSwedish = `${blobURL}/Allmänt avtal för Egenanställning.pdf`;
    const workAgrementEnglish = `${blobURL}/General Contract for Self-Employment.pdf`;
    const [isPasswordVisible, setIsPasswordVisible] = useState(false);
    const dropdownRef = useOutsideClick(() => setShowDOBCalendar(false)) as React.MutableRefObject<HTMLDivElement | null>;
    const params = new URLSearchParams(window.location.search);
    const referredByCode = params.get('inviteCode') || params.get('invitecode') || '';
    const referredByUserId = params.get('userId') || '';

    if (referredByCode) setItem('inviteCode', referredByCode);
    if (referredByUserId) setItem('userId', referredByUserId);
    const tduid = getItem('tduid') ?? null

    const [formData, setFormData] = useState<RegistrationFormData>({
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        socialSecurityNumber: '',
        country: '',
        dateOfBirth: '',
        agreeToTerms: false,
        noIdNumber: false,
        zipCode: '',
        address: '',
        phoneNo: '',
        accountType: 'freelancer',
        ReferredByCode: referredByCode,
        ReferredByUserId: referredByUserId,
    });

    const [errors, setErrors] = useState<FormErrors>({});

    const accountTypes = [
        { key: 'freelancer', icon: FreelancerIcon, label: t("REGISTRATION_FREELANCER") },
        { key: 'business', icon: BusinessIcon, label: t("REGISTRATION_BUSINESS") }
    ];


    const handleInputChange = (field: keyof RegistrationFormData, value: string | boolean) => {
        setFormData(prev => {
            const newData = {
                ...prev,
                [field]: value
            };

            if (field === 'noIdNumber') {
                if (value) {
                    newData.socialSecurityNumber = '';
                } else {
                    newData.dateOfBirth = '';
                }
            }

            return newData;
        });

        const newErrors = { ...errors };
        if (field === 'firstName' && typeof value === 'string') {
            const trimmedValue = value.trim();

            if (!trimmedValue) {
                newErrors.firstName = '';
            }
            else if (!validateLatinCharacters(trimmedValue)) {
               newErrors.firstName = "FIRST_NAME_LATIN_ONLY";
            }
            else {
                newErrors.firstName = '';
            }
        }
        if (field === 'lastName' && typeof value === 'string') {
            const trimmedValue = value.trim();

            if (!trimmedValue) {
                newErrors.lastName = '';
            }
            else if (!validateLatinCharacters(trimmedValue)) {
               newErrors.lastName = "LAST_NAME_LATIN_ONLY";
            }
            else {
                newErrors.lastName = '';
            }
        }

        if (field === 'email' && typeof value === 'string') {
            const trimmedValue = value.trim();

            // No error when empty (avoid red highlight)
            if (!trimmedValue) {
                newErrors.email = '';
            }
            // Show error only when user entered an invalid email
            else if (!validateEmail(trimmedValue)) {
                newErrors.email = "EMAIL_INVALID";
            }
            else {
                newErrors.email = '';
            }
        }

        if (field === 'password' && typeof value === 'string') {
            const trimmedValue = value.trim();

            // No error when empty
            if (!trimmedValue) {
                newErrors.password = '';
            }
            // Error only when user entered an invalid password
            else if (!validatePassword(trimmedValue)) {
                newErrors.password = "PASSWORD_RULE";
            }
            else {
                newErrors.password = '';
            }
        }

        if (field === 'zipCode' && typeof value === 'string') {
            const trimmedValue = value.trim();

            // No error when empty
            if (!trimmedValue) {
                newErrors.zipCode = '';
            }
            // Error only if user typed invalid postal code
            else if (!validatePostalCode(trimmedValue)) {
                newErrors.zipCode = "POSTAL_CODE_INVALID";
            }
            else {
                newErrors.zipCode = '';
            }
        }

        if (field === 'phoneNo' && typeof value === 'string') {
            const trimmedValue = value.trim();

            if (!trimmedValue) {
                newErrors.phoneNo = '';
            }
            else if (!validatePhoneNumber(trimmedValue)) {
                newErrors.phoneNo = "INVALID_PHONE_NUMBER";
            }
            else {
                newErrors.phoneNo = '';
            }
        }


        if (field === 'agreeToTerms' && typeof value === 'boolean') {
            if (!value) {
                newErrors.agreeToTerms = "PLEASE_SELECT_TERMS_AND_CONDITION";
            } else {
                newErrors.agreeToTerms = '';
            }
        }

        if (field === 'noIdNumber') {
            newErrors.socialSecurityNumber = '';
            newErrors.dateOfBirth = '';
        }

        setErrors(newErrors);
    };

      async function hashEmail(email: string) {
        const hash = CryptoJS.SHA256(email.trim().toLowerCase());
        return hash.toString(CryptoJS.enc.Hex);
      }
    
      async function pushHashedEmail(email: string) {
        const isProduction = import.meta.env.VITE_NODE_ENV === "production";
        if (isProduction) {
          const hashedEmail = await hashEmail(email);
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({
            event: "create_account",
            email: hashedEmail,
          });
        }
      }
    const onSubmit = useCallback(async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm(formData, t, setErrors)) {
            toast.error(t("PLEASE_FILL_ALL_REQUIRED_FIELDS_CORRECTLY") as string);
            return;
        }

        try {
            setLoading(true);
            const registrationPayload = prepareRegistrationPayload(formData, accountType, currentLanguage , tduid);
            const resolvedReferredByCode = referredByCode || getItem('inviteCode') || '';
            const resolvedReferredByUserId = referredByUserId || getItem('userId') || '';

            if (resolvedReferredByCode) {
                registrationPayload.ReferredByCode = resolvedReferredByCode;
                registrationPayload.ReferredByUserId = resolvedReferredByUserId;
            }
            const response = await dispatch(registerUser(registrationPayload));

            if (response.type === 'signup/registerUser/fulfilled') {
                toast.success(t("REGISTRATION_SUCCESSFUL") as string);
                const resp = await dispatch(signInWithEmailAndPassword({
                    email: formData.email,
                    password: formData.password,
                }));
                if (resp.type === 'signin/signInWithEmailAndPassword/fulfilled') {
                    window.dataLayer = window.dataLayer || [];
                    await pushHashedEmail(formData.email);
                    navigate('/v2/dashboard?lang=' + currentLanguage);
                    return;
                }
                if (resp.type === 'signin/signInWithEmailAndPassword/rejected') {
                    const errorMessage = resp.payload ?? t("SIGNIN_FAILED") as string;
                    toast.error(t(errorMessage) as string);
                }
            } else {
                const errorMessage = response.payload || submitError || t("Registration failed. Please try again.") as string;
                toast.error(t(errorMessage) as string);
            }
        } catch (error) {
            toast.error(t("REGISTRATION_ERROR") as string);
            console.error("Registration error:", error);
        } finally {
            setLoading(false);
        }
    }, [formData, validateForm, accountType, currentLanguage, dispatch, submitError, t]);

    const handleSocialLogin = (provider: string) => {
        if (provider === 'bankid') {
            setSignupMode('bankid-active');
        }
    };

    const handleSSNInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = handleSSNInput(e.target.value);
        handleInputChange('socialSecurityNumber', value);
    };

    const handleDOBInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const currentValue = e.target.value.replace(/\D/g, '');
        if (currentValue.length <= 8) {
            const value = handleDOBInput(e.target.value);
            handleInputChange('dateOfBirth', value);
        }
    };

    const handleDOBInputFocus = () => {
        setShowDOBCalendar(true);
    };


    return (
        <div className="registration-page-wrapper">
            <RegistrationHeader />
            <Container>
                <Row>
                    <Col sm={12} md={6} className="px-0 ">
                        <div className="max-width-600 pr-3">
                            <h1 className="registration-title">{t("REGISTRATION_TITLE")}</h1>

                            {signupMode === 'bankid-active' && (
                                <div className="bankid-signup-wrapper">
                                    <p className="bankid-signup-subtitle">{t("BANKID_SIGNUP_SUBTITLE")}</p>
                                    <BankIDSignupInline accountType={accountType} onSuccess={() => {}} />
                                    <div className="sign-up-section" style={{ marginTop: '16px' }}>
                                        <span>{t("REGISTRATION_ALREADY_HAVE_ACCOUNT")}</span>
                                        <Link to={getLocalizedPathFromLocalStorage({ get: (key: string) => getItem(key) ?? null }, { 'sv-se': 'e-postinloggning', 'en-us': 'emaillogin' })} className="sign-up-link">{t("REGISTRATION_SIGN_IN")}</Link>
                                    </div>

                                    <div className="signin-or-divider">
                                        <span className="signin-or-line" />
                                        <span className="signin-or-text">or</span>
                                        <span className="signin-or-line" />
                                    </div>
                                    <p className="signin-bankid-hint">
                                        {t("SIGNUP_BANKID_HINT")}
                                    </p>
                                    <button
                                        type="button"
                                        className="signin-email-fallback-btn"
                                        onClick={() => setSignupMode('email')}
                                    >
                                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <rect x="3" y="5" width="18" height="14" rx="2" stroke="currentColor" strokeWidth="2"/>
                                            <path d="M3 8l9 6 9-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                        <span>{t("SIGN_UP_WITH_EMAIL")}</span>
                                    </button>
                                </div>
                            )}

                            {signupMode === 'email' && (
                            <div className="registration-form-container">
                                <div className="account-type-section">
                                    <div className="account-type-label">
                                        {t("REGISTRATION_ACCOUNT_TYPE_QUESTION")}
                                    </div>
                                    <div className="account-type-toggle">
                                        {accountTypes.map(({ key, icon, label }) => (
                                            <button
                                                key={key}
                                                type="button"
                                                className={`account-type-btn ${accountType === key ? 'active' : ''}`}
                                                onClick={() => {
                                                    setAccountType(key as 'freelancer' | 'business');
                                                    handleInputChange('accountType', key);
                                                }}
                                            >
                                                <div className="account-type-icon">
                                                    <img src={icon} alt={label} />
                                                </div>
                                                <span>{label}</span>
                                            </button>
                                        ))}
                                    </div>


                                </div>
                                <form className="registration-form" onSubmit={onSubmit}>
                                    <UIInput
                                        label={t("REGISTRATION_FIRST_NAME_LABEL")}
                                        placeholder={t("REGISTRATION_FIRST_NAME_PLACEHOLDER")}
                                        value={formData.firstName}
                                        onChange={(e) => handleInputChange('firstName', e.target.value)}
                                        error={!!errors.firstName}
                                        errorMessage={errors.firstName ? t(errors.firstName) : ''}
                                    />
                                    <UIInput
                                        label={t("REGISTRATION_LAST_NAME_LABEL")}
                                        placeholder={t("REGISTRATION_LAST_NAME_PLACEHOLDER")}
                                        value={formData.lastName}
                                        onChange={(e) => handleInputChange('lastName', e.target.value)}
                                        error={!!errors.lastName}
                                        errorMessage={errors.lastName ? t(errors.lastName) : ''}
                                    />

                                    {/* Email Field */}
                                    <UIInput
                                        type="email"
                                        label={t("REGISTRATION_EMAIL_LABEL")}
                                        placeholder={t("REGISTRATION_EMAIL_PLACEHOLDER")}
                                        value={formData.email}
                                        onChange={(e) => handleInputChange('email', e.target.value)}
                                        error={!!errors.email}
                                        errorMessage={errors.email ? t(errors.email) : ''}
                                    />

                                    {/* Password Field */}
                                    <UIInput
                                        type={isPasswordVisible ? 'text' : 'password'}
                                        label={t("REGISTRATION_PASSWORD_LABEL")}
                                        placeholder={t("REGISTRATION_PASSWORD_PLACEHOLDER")}
                                        value={formData.password}
                                        onChange={(e) => handleInputChange('password', e.target.value)}
                                        error={!!errors.password}
                                        errorMessage={errors.password ? t(errors.password) : ''}
                                        onIconClick={() => setIsPasswordVisible(!isPasswordVisible)}
                                        icon={
                                            <svg xmlns="http://www.w3.org/2000/svg" width="21" height="20" viewBox="0 0 21 20" fill="none">
                                                <path d="M2.92442 10.9778C2.63978 10.3585 2.63978 9.6441 2.92442 9.02484C4.242 6.15832 7.13866 4.16797 10.5002 4.16797C13.8617 4.16797 16.7584 6.15833 18.076 9.02484C18.3606 9.6441 18.3606 10.3585 18.076 10.9778C16.7584 13.8443 13.8617 15.8346 10.5002 15.8346C7.13866 15.8346 4.24201 13.8443 2.92442 10.9778Z" stroke="black" strokeWidth="2" />
                                                <path d="M13.0002 10.0013C13.0002 11.382 11.8809 12.5013 10.5002 12.5013C9.11948 12.5013 8.00019 11.382 8.00019 10.0013C8.00019 8.62059 9.11948 7.5013 10.5002 7.5013C11.8809 7.5013 13.0002 8.62059 13.0002 10.0013Z" stroke="black" strokeWidth="2" />
                                            </svg>
                                        }
                                    />

                                    {/* Social Security Number Field */}
                                    {!formData.noIdNumber && (
                                        <UIInput
                                            type="text"
                                            label={t("REGISTRATION_SSN_LABEL")}
                                            placeholder={t("REGISTRATION_SSN_PLACEHOLDER")}
                                            value={formData.socialSecurityNumber}
                                            onChange={handleSSNInputChange}
                                            error={!!errors.socialSecurityNumber}
                                            errorMessage={errors.socialSecurityNumber ? t(errors.socialSecurityNumber) : ''}
                                        />
                                    )}
                                    {/* No ID Number Checkbox */}
                                    <div className="checkbox-wrapper">
                                        <label className="checkbox-label">
                                            <input
                                                type="checkbox"
                                                className="checkbox-input"
                                                checked={formData.noIdNumber}
                                                onChange={(e) => handleInputChange('noIdNumber', e.target.checked)}
                                            />
                                            <span className="checkbox-checkmark"></span>
                                            <span className="checkbox-text">{t("REGISTRATION_NO_ID_NUMBER")}</span>
                                        </label>
                                    </div>
                                    {/* Date of Birth Field */}
                                    {formData?.noIdNumber && (
                                        <>
                                            <UIInput
                                                type="text"
                                                label={t("ENTER_DATE_OF_BIRTH")}
                                                placeholder="YYYYMMDD"
                                                value={formData.dateOfBirth}
                                                onChange={handleDOBInputChange}
                                                onFocus={handleDOBInputFocus}
                                                error={!!errors.dateOfBirth}
                                                errorMessage={errors.dateOfBirth ? t(errors.dateOfBirth) : ''}
                                                icon={
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        width={17}
                                                        height={19}
                                                        viewBox="0 0 17 19"
                                                        fill="none"
                                                    >
                                                        <path
                                                            d="M1 7.66667H16M5.16667 4.33333V1M11.8333 4.33333V1M6.33333 17.6667H10.6667C12.5335 17.6667 13.4669 17.6667 14.18 17.3034C14.8072 16.9838 15.3171 16.4738 15.6367 15.8466C16 15.1336 16 14.2002 16 12.3333V8C16 6.13316 16 5.19974 15.6367 4.4867C15.3171 3.85949 14.8072 3.34956 14.18 3.02998C13.4669 2.66667 12.5335 2.66667 10.6667 2.66667H6.33333C4.46649 2.66667 3.53307 2.66667 2.82003 3.02998C2.19282 3.34956 1.68289 3.85949 1.36331 4.4867C1 5.19974 1 6.13316 1 8V12.3333C1 14.2002 1 15.1336 1.36331 15.8466C1.68289 16.4738 2.19282 16.9838 2.82003 17.3034C3.53307 17.6667 4.46649 17.6667 6.33333 17.6667Z"
                                                            stroke="black"
                                                            strokeWidth={2}
                                                            strokeLinecap="round"
                                                        />
                                                    </svg>

                                                }
                                            />

                                            {/* DOB Calendar Modal */}
                                            {showDOBCalendar && (
                                                <div className="calendar-modal-overlay" ref={dropdownRef} onClick={() => setShowDOBCalendar(false)}>
                                                    <div className="calendar-modal-content" onClick={(e) => e.stopPropagation()}>
                                                        <div className="calendar-modal-header">
                                                            <h3>{t("SELECT_DATE_OF_BIRTH") || "Select Date of Birth"}</h3>
                                                            <button
                                                                type="button"
                                                                className="calendar-modal-close"
                                                                onClick={() => setShowDOBCalendar(false)}
                                                            >
                                                                ×
                                                            </button>
                                                        </div>
                                                        <CalendarSingle
                                                            onDateSelect={(date) => {
                                                                const year = date.getFullYear();
                                                                const month = String(date.getMonth() + 1).padStart(2, '0');
                                                                const day = String(date.getDate()).padStart(2, '0');
                                                                const formattedDate = `${year}${month}${day}000`;
                                                                handleInputChange('dateOfBirth', formattedDate);
                                                                setShowDOBCalendar(false);
                                                            }}
                                                            label="Date of Birth"
                                                        />
                                                    </div>
                                                </div>
                                            )}
                                        </>
                                    )}
                                    <UIInput
                                        type="text"
                                        label={t("PHONE_NUMBER_LABEL")}
                                        placeholder={t("PHONE_NUMBER_LABEL")}
                                        value={formData.phoneNo}
                                        onChange={(e) => handleInputChange('phoneNo', e.target.value)}
                                        error={!!errors.phoneNo}
                                        errorMessage={errors.phoneNo ? t(errors.phoneNo) : ''}
                                        inputMode="numeric"
                                        pattern="[0-9]*"
                                        maxLength={13}
                                    />
                                      <div className="form-group">
                                        <label className="form-label">{t('COUNTRY')}</label>
                                        <select
                                          className={`form-select`}
                                          value={formData.country}
                                          onChange={(e) => handleInputChange('country', e.target.value)}
                                          disabled={loading}
                                        >
                                          <option value="">{t('SELECT_YOUR_COUNTRY')}</option>
                                          <option value={"Sweden"}>{"Sweden"}</option>
                                          <option value={"USA"}>{"USA"}</option>
                                          {renderCountryOptionsWithI18n(t)}
                                        </select>
                                    </div>
                                    <UIInput
                                        type="text"
                                        label={t("POSTAL_CODE_LABEL")}
                                        placeholder={t("POSTAL_CODE_LABEL")}
                                        maxLength={10}
                                        value={formData.zipCode}
                                        onChange={(e) => {
                                            const sanitizedValue = e.target.value.replace(/[^A-Za-z0-9\s,-]/g, '');
                                            handleInputChange('zipCode', sanitizedValue);
                                        }}
                                    // error={!!errors.socialSecurityNumber}
                                    // errorMessage={errors.socialSecurityNumber}
                                    />
                                    <UIInput
                                        type="text"
                                        label={t("ADDRESS_LABEL")}
                                        placeholder={t("ADDRESS_LABEL")}
                                        value={formData.address}
                                        onChange={(e) => handleInputChange('address', e.target.value)}
                                        error={!!errors.address}
                                        errorMessage={errors.address ? t(errors.address) : ''}
                                    />
                                    {/* Terms Agreement */}
                                    <div className="checkbox-wrapper">
                                        <label className="checkbox-label">
                                            <input
                                                type="checkbox"
                                                className="checkbox-input"
                                                checked={formData.agreeToTerms}
                                                onChange={(e) => handleInputChange('agreeToTerms', e.target.checked)}
                                            />
                                            <span className="checkbox-checkmark"></span>
                                            <span className="checkbox-text">
                                                {t("REGISTRATION_TERMS_TEXT_1")}

                                                <Link
                                                    to={currentLanguage === LOCALES.EN_US
                                                        ? workAgrementEnglish
                                                        : workAgrementSwedish
                                                    }
                                                    target="_blank"
                                                >
                                                    {t("REGISTRATION_TERMS_LINK_1")}
                                                </Link>
                                                {t("REGISTRATION_TERMS_TEXT_2")}
                                                <Link
                                                    to={currentLanguage === LOCALES.EN_US
                                                        ? invoicePDF
                                                        : invoiceSwidishPDF
                                                    }
                                                    target="_blank"
                                                >
                                                    {t("REGISTRATION_TERMS_LINK_2")}</Link>
                                                {t("REGISTRATION_TERMS_TEXT_3")}
                                            </span>
                                        </label>
                                    </div>

                                    {/* Display Form Errors */}
                                    {errors.agreeToTerms && (
                                        <div className="error-message" style={{ color: 'red', marginBottom: '1rem', textAlign: 'center' }}>
                                            {t(errors.agreeToTerms)}
                                        </div>
                                    )}

                                    {/* Display Redux Error */}
                                    {submitError && (
                                        <div className="error-message" style={{ color: 'red', marginBottom: '1rem', textAlign: 'center' }}>
                                            {t(submitError)}
                                        </div>
                                    )}

                                    <button type="submit" className="submit-btn" disabled={loading || isSubmitting}>
                                        {(loading || isSubmitting) ? t("REGISTRATION_SUBMITTING") : t("REGISTRATION_SUBMIT")}
                                    </button>
                                    <div className="sign-up-section">
                                        <span>{t("REGISTRATION_ALREADY_HAVE_ACCOUNT")}</span>
                                        <Link to={getLocalizedPathFromLocalStorage({ get: (key: string) => getItem(key) ?? null }, { 'sv-se': 'e-postinloggning', 'en-us': 'emaillogin' })} className="sign-up-link">{t("REGISTRATION_SIGN_IN")}</Link>
                                    </div>
                                    <div className="trustpilot-widget" data-locale="sv-SE" data-template-id="5419b732fbfb950b10de65e5" data-businessunit-id="64f712e3dbd20bbfcf3a825f" data-style-height="24px" data-style-width="100%">
                                        <a href="https://se.trustpilot.com/review/invozio.com" target="_blank" rel="noopener">Trustpilot</a>
                                    </div>
                                </form>
                                <div className="signin-or-divider" style={{ marginTop: '8px' }}>
                                    <span className="signin-or-line" />
                                    <span className="signin-or-text">or</span>
                                    <span className="signin-or-line" />
                                </div>
                                <button
                                    type="button"
                                    className="signin-email-fallback-btn"
                                    onClick={() => setSignupMode('bankid-active')}
                                >
                                    <img src={bankidcolor} alt="" width={15} height={15} style={{ objectFit: 'contain' }} />
                                    <span>{t("SIGNUP_WITH_BANKID")}</span>
                                </button>
                            </div>
                            )}
                        </div>
                    </Col>
                    <Col sm={12} md={6} className="px-0 d-none d-sm-block">
                        <EconomicCard />
                    </Col>

                </Row>
            </Container >

        </div >
    )
}


export default Registration
